import { Link } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { ScreenataPageLayout } from '@/components/compliance/ScreenataPageLayout'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { cn } from '@/lib/utils'

function PolicySubmissionMetaBarSkeleton() {
  return (
    <div className='border-b border-border bg-surface-subtle px-6 py-3'>
      <div className='flex flex-wrap items-center gap-4'>
        <Skeleton className='h-4 w-32 rounded-sm' />
        <Skeleton className='h-4 w-24 rounded-sm' />
        <Skeleton className='h-4 w-36 rounded-sm' />
        <Skeleton className='h-4 w-44 rounded-sm' />
      </div>
    </div>
  )
}

export function PolicyMetadataBarSkeleton() {
  return (
    <div className='flex items-center gap-4 border-b border-border bg-surface-subtle px-4 py-1.5'>
      {Array.from({ length: 4 }, (_, index) => (
        <Skeleton key={index} className='h-6 w-28 rounded-sm' />
      ))}
    </div>
  )
}

/** Same min height as loaded `PolicyContentViewer` paper. */
const POLICY_DETAIL_PAPER_CLASS =
  'min-h-[calc(var(--app-vh)_-_200px)] w-full max-w-[816px] rounded-sm shadow-[0_2px_12px_rgba(0,0,0,0.06)]'

/** Fills remaining main column below page header — metadata + doc stack inside. */
export const POLICY_DETAIL_PAGE_BODY_CLASS =
  'flex min-h-0 flex-1 flex-col overflow-hidden'

const POLICY_DETAIL_PAPER_OVERLAY_CLASS =
  'min-h-[480px] w-full max-w-[816px] rounded-sm shadow-[0_2px_12px_rgba(0,0,0,0.06)]'

function PolicyDetailDocumentBody({
  className,
  embedInOverlay = false,
}: {
  className?: string
  embedInOverlay?: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 justify-center bg-muted px-20 py-10',
        className,
      )}
    >
      <Skeleton
        className={
          embedInOverlay ? POLICY_DETAIL_PAPER_OVERLAY_CLASS : POLICY_DETAIL_PAPER_CLASS
        }
      />
    </div>
  )
}

/**
 * Policy detail document skeleton — one layout for full-page load and in-panel doc fetch.
 * `embedded`: only the gray + paper block (inside `PolicyDetailPanel`).
 */
export function PolicyDetailDocumentSkeleton({
  embedded = false,
  embedInOverlay = false,
  className,
}: {
  embedded?: boolean
  className?: string
  /** Match `PolicyContentViewer` min-height in main-panel overlay. */
  embedInOverlay?: boolean
}) {
  if (embedded) {
    return (
      <div
        className={cn('relative min-h-0 flex-1 w-full', className)}
        aria-busy
        aria-label='Loading policy content'
      >
        <div className='absolute inset-0 flex flex-col'>
          <PolicyDetailDocumentBody
            className='min-h-0 flex-1'
            embedInOverlay={embedInOverlay}
          />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'bg-background relative flex min-h-0 flex-1 flex-col',
        className,
      )}
      aria-busy
      aria-label='Loading policy content'
    >
      <PolicyDetailDocumentBody embedInOverlay={embedInOverlay} />
    </div>
  )
}

/** @deprecated Use `PolicyDetailDocumentSkeleton` with `embedded`. */
export const PolicyDetailDocumentAreaSkeleton = PolicyDetailDocumentSkeleton

type AuditsHomeRoute =
  | '/workspaces/$workspaceId/audits'
  | '/workspaces/$workspaceId/auditor-center'

export type PolicyDetailLoadingShellProps = {
  workspaceId: string
  variant: 'manage' | 'auditor'
  auditCycleId?: string
  auditsHomeRoute?: AuditsHomeRoute
  adminAuditorPreview?: {
    workspaceId: string
    auditCycleId: string
  } | null
}

export function PolicyDetailLoadingShell({
  workspaceId,
  variant,
  auditCycleId,
  auditsHomeRoute = '/workspaces/$workspaceId/auditor-center',
  adminAuditorPreview,
}: PolicyDetailLoadingShellProps) {
  const isAuditor = variant === 'auditor'

  const breadcrumb = isAuditor ? (
    auditCycleId ? (
      <div className='flex min-w-0 items-center gap-1.5 text-[13px]'>
        <Link
          to={auditsHomeRoute}
          params={{ workspaceId }}
          preload={false}
          className='text-text-body transition-colors hover:text-text-secondary'
        >
          Audits
        </Link>
        <ChevronRight className='size-3 shrink-0 text-text-placeholder' aria-hidden />
        <Link
          to='/workspaces/$workspaceId/auditor-center/$auditCycleId/policies'
          params={{ workspaceId, auditCycleId }}
          preload={false}
          className='text-text-body transition-colors hover:text-text-secondary'
        >
          Policies
        </Link>
        <ChevronRight className='size-3 shrink-0 text-text-placeholder' aria-hidden />
        <Skeleton className='h-4 w-40 max-w-[200px] rounded-sm' />
      </div>
    ) : null
  ) : (
    <div className='flex min-w-0 items-center gap-2'>
      <Link
        to='/workspaces/$workspaceId/policies'
        params={{ workspaceId }}
        className='font-heading text-[15px] font-medium text-text-subtle hover:text-cobalt-strong'
      >
        Policies
      </Link>
      <ChevronRight
        className='h-3.5 w-3.5 shrink-0 text-text-placeholder'
        aria-hidden
      />
      <Skeleton className='h-4 w-48 max-w-[min(40vw,320px)] rounded-sm' />
    </div>
  )

  return (
    <ScreenataPageLayout
      title={'\u00a0'}
      breadcrumb={breadcrumb}
      breadcrumbIsTerminal
      headerExtra={<Skeleton className='h-7 w-7 shrink-0 rounded-sm' />}
      headerActions={
        <div className='flex items-center gap-2'>
          <Skeleton className='h-7 w-24 rounded-sm' />
          <Skeleton className='h-7 w-28 rounded-sm' />
        </div>
      }
      mainClassName='flex h-[calc(var(--app-vh)_-_50px)] min-h-0 flex-col overflow-hidden'
      raw
    >
      <div className={POLICY_DETAIL_PAGE_BODY_CLASS}>
        {isAuditor ? <PolicySubmissionMetaBarSkeleton /> : null}
        <PolicyMetadataBarSkeleton />
        <PolicyDetailDocumentSkeleton />
      </div>
    </ScreenataPageLayout>
  )
}

/** @deprecated Use `PolicyDetailLoadingShell` with `variant="auditor"`. */
export type AuditorPolicyDetailLoadingShellProps = Omit<
  PolicyDetailLoadingShellProps,
  'variant'
> & {
  auditCycleId: string
  auditsHomeRoute: AuditsHomeRoute
}

export function AuditorPolicyDetailLoadingShell(
  props: AuditorPolicyDetailLoadingShellProps,
) {
  return <PolicyDetailLoadingShell variant='auditor' {...props} />
}

/** @deprecated Use `PolicyDetailLoadingShell` with `variant="manage"`. */
export type ManagePolicyDetailLoadingShellProps = {
  workspaceId: string
}

export function ManagePolicyDetailLoadingShell({
  workspaceId,
}: ManagePolicyDetailLoadingShellProps) {
  return (
    <PolicyDetailLoadingShell variant='manage' workspaceId={workspaceId} />
  )
}
